import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores';
import { API_URL, formatCurrency, formatDate, getStatusColor, INVOICE_STATUSES, PAYMENT_METHODS } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  FileText,
  Download,
  CheckCircle2,
  Lock,
  CreditCard,
  ArrowDownToLine,
  Filter,
} from 'lucide-react';

const InvoiceCard = ({ invoice, onFinalize, onPay, onDownloadPDF }) => {
  const statusLabel = INVOICE_STATUSES.find((s) => s.value === invoice.status)?.label || invoice.status;

  return (
    <Card className="card-hover" data-testid={`invoice-card-${invoice.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3 className="font-medium font-heading">
                {invoice.invoice_number || 'Brouillon'}
              </h3>
              <Badge variant="outline" className={getStatusColor(invoice.status)}>
                {statusLabel}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">{invoice.client_name || 'Sans client'}</p>
            {invoice.quote_reference && (
              <p className="text-xs text-muted-foreground mt-0.5">Devis: {invoice.quote_reference}</p>
            )}
            <p className="text-xs text-muted-foreground mt-0.5">
              {formatDate(invoice.invoice_date)}
              {invoice.due_date && ` - Ech. ${formatDate(invoice.due_date)}`}
            </p>
          </div>

          <div className="text-right shrink-0">
            <p className="text-lg font-bold font-heading">{formatCurrency(invoice.total_ht)}</p>
            <p className="text-xs text-muted-foreground">HT</p>
            {invoice.total_ttc !== invoice.total_ht && (
              <p className="text-sm text-muted-foreground">{formatCurrency(invoice.total_ttc)} TTC</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDownloadPDF(invoice)}
            data-testid={`download-invoice-pdf-${invoice.id}`}
          >
            <Download className="w-3.5 h-3.5 mr-1.5" /> PDF
          </Button>
          {invoice.status === 'draft' && (
            <Button
              size="sm"
              onClick={() => onFinalize(invoice)}
              data-testid={`finalize-invoice-${invoice.id}`}
            >
              <Lock className="w-3.5 h-3.5 mr-1.5" /> Finaliser
            </Button>
          )}
          {invoice.status === 'finalized' && (
            <Button
              size="sm"
              variant="default"
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => onPay(invoice)}
              data-testid={`pay-invoice-${invoice.id}`}
            >
              <CreditCard className="w-3.5 h-3.5 mr-1.5" /> Marquer payée
            </Button>
          )}
          {invoice.status === 'paid' && (
            <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Payée
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const PaymentDialog = ({ invoice, open, onOpenChange, onConfirm }) => {
  const [paymentMethod, setPaymentMethod] = useState('transfer');
  const [paymentRef, setPaymentRef] = useState('');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Enregistrer le paiement</DialogTitle>
          <DialogDescription>
            Facture {invoice?.invoice_number} - {formatCurrency(invoice?.total_ttc || invoice?.total_ht)}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Mode de paiement</Label>
            <Select value={paymentMethod} onValueChange={setPaymentMethod}>
              <SelectTrigger data-testid="payment-method-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Référence (optionnel)</Label>
            <Input
              value={paymentRef}
              onChange={(e) => setPaymentRef(e.target.value)}
              placeholder="N° chèque, réf. virement..."
              data-testid="payment-reference-input"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => {
                onConfirm({ payment_method: paymentMethod, payment_reference: paymentRef });
                onOpenChange(false);
                setPaymentMethod('transfer');
                setPaymentRef('');
              }}
              data-testid="confirm-payment-btn"
            >
              Confirmer le paiement
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const InvoicesPage = () => {
  const { token } = useAuthStore();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('all');
  const [payDialogInvoice, setPayDialogInvoice] = useState(null);

  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ['invoices', statusFilter],
    queryFn: async () => {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const res = await fetch(`${API_URL}/invoices${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch invoices');
      return res.json();
    },
  });

  const finalizeMutation = useMutation({
    mutationFn: async (invoiceId) => {
      const res = await fetch(`${API_URL}/invoices/${invoiceId}/finalize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ confirm: true }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Erreur');
      }
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      toast.success(`Facture finalisée: ${data.invoice_number}`);
    },
    onError: (err) => toast.error(err.message),
  });

  const payMutation = useMutation({
    mutationFn: async ({ invoiceId, payment }) => {
      const res = await fetch(`${API_URL}/invoices/${invoiceId}/pay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payment),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Erreur');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      toast.success('Paiement enregistré');
    },
    onError: (err) => toast.error(err.message),
  });

  const handleDownloadPDF = async (invoice) => {
    try {
      const res = await fetch(`${API_URL}/invoices/${invoice.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('PDF error');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `facture_${invoice.invoice_number || 'brouillon'}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF téléchargé');
    } catch {
      toast.error('Erreur lors du téléchargement');
    }
  };

  //Fonction utilitaire pour éviter le bug du J-1 dû aux fuseaux horaires : on force les heures à midi (12:00) pour que la date envoyée soit correcte même si l'utilisateur est dans un fuseau horaire différent
  const toSafeISOString = (date) => {
    if (!date) return null;
    const safeDate = new Date(date);
    // On force l'heure à 12:00:00 (Midi) LOCALE
    safeDate.setHours(12, 0, 0, 0); 
    return safeDate.toISOString();
};

  const handleExportCSV = async () => {
    try {
      const res = await fetch(`${API_URL}/exports/invoices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Export error');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `export_factures_${toSafeISOString(new Date()).slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export CSV téléchargé');
    } catch {
      toast.error('Erreur lors de l\'export');
    }
  };

  const handleFinalize = (invoice) => {
    if (window.confirm(`Finaliser cette facture ? Un numéro séquentiel sera attribué et elle deviendra immuable.`)) {
      finalizeMutation.mutate(invoice.id);
    }
  };

  // KPIs
  const totalHT = invoices.reduce((s, i) => s + (i.total_ht || 0), 0);
  const paidCount = invoices.filter((i) => i.status === 'paid').length;
  const pendingCount = invoices.filter((i) => i.status === 'finalized').length;

  return (
    <div className="space-y-6" data-testid="invoices-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold">Factures</h1>
          <p className="text-muted-foreground">Gestion et suivi de vos factures</p>
        </div>
        <Button variant="outline" onClick={handleExportCSV} data-testid="export-csv-btn">
          <ArrowDownToLine className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Total facturé HT</p>
            <p className="text-2xl font-heading font-bold mt-1">{formatCurrency(totalHT)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">En attente</p>
            <p className="text-2xl font-heading font-bold mt-1 text-blue-500">{pendingCount}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Payées</p>
            <p className="text-2xl font-heading font-bold mt-1 text-emerald-500">{paidCount}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <Filter className="w-4 h-4 text-muted-foreground" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="invoice-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes</SelectItem>
            {INVOICE_STATUSES.map((s) => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Invoice List */}
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <Card key={i}><CardContent className="p-4"><Skeleton className="h-20 w-full" /></CardContent></Card>
          ))}
        </div>
      ) : invoices.length > 0 ? (
        <div className="space-y-3">
          {invoices.map((invoice) => (
            <InvoiceCard
              key={invoice.id}
              invoice={invoice}
              onFinalize={handleFinalize}
              onPay={(inv) => setPayDialogInvoice(inv)}
              onDownloadPDF={handleDownloadPDF}
            />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <FileText className="w-12 h-12 mx-auto text-muted-foreground/40 mb-4" />
            <h3 className="text-lg font-medium mb-1">Aucune facture</h3>
            <p className="text-sm text-muted-foreground">
              Les factures sont créées depuis un devis signé.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Payment Dialog */}
      <PaymentDialog
        invoice={payDialogInvoice}
        open={!!payDialogInvoice}
        onOpenChange={(open) => !open && setPayDialogInvoice(null)}
        onConfirm={(payment) => {
          payMutation.mutate({ invoiceId: payDialogInvoice.id, payment });
          setPayDialogInvoice(null);
        }}
      />
    </div>
  );
};

export default InvoicesPage;
